import ReactDOM from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { ensureRegistrationListeners } from '@utils/pushNotificationService';
import Prompt from './Prompt.react';
import Icon from './Icon.react';
import AuthStore from '@stores/AuthStore';
import UserStore from '@stores/UserStore';
import UserActions from '@actions/UserActions';
import Analytics from '@utils/Analytics';
import store from 'store';
import './PushNotificationPrompt.scss';

const LOCALSTORAGE_KEY = 'push-notifications';
export const LOCALSTORAGE_KEY_DEVICE_TOKEN = 'push-notifications-device-key';

export const storeTokenInBackend = async (token, platform) => {
    try {
        const url = UserStore.getLinks().devices;
        const request = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=device/1' },
            body: JSON.stringify({ token, platform }),
        };
        const response = await AuthStore.fetch(url, request);
        if (!response.ok) {
            throw new Error('Failed to store token in backend.');
        }
        console.log('Token successfully stored in backend!');
    } catch (err) {
        console.error('Error storing token in backend:', err.message);
    }
};

export const updatePushInhibitedSetting = async (inhibited, enableSchedules = false) => {
    const payload = { inhibit_push_notification: inhibited };
    if (!inhibited && enableSchedules) {
        payload.daily_push_notification = true;
        payload.weekly_push_notification = true;
    }
    return UserActions.updateSpecificMeta(payload);
};

const renderDeniedContent = () => {
    const platform = Capacitor.getPlatform();

    return (
        <>
            <h2>Stay on Track with EatLove</h2>
            <p>
                You’ve turned off notifications at the system level—so you’re missing meal reminders and grocery alerts.
            </p>

            {platform === 'ios' ? (
                <p>
                    To re‑enable, go to <strong>Settings &gt; Notifications &gt; EatLove</strong> and turn on{' '}
                    <strong>Allow Notifications</strong>.
                </p>
            ) : (
                <p>
                    To re‑enable, open <strong>Settings &gt; Apps &gt; EatLove &gt; Notifications</strong> and toggle on{' '}
                    <strong>All EatLove notifications</strong>.
                </p>
            )}
        </>
    );
};

const PushNotificationPrompt = ({
    onClose = () => {},
    onPermissionResult = () => {},
    force = false,
    delay = 0,
    triggeredFrom = 'unknown',
}) => {
    const platform = Capacitor.getPlatform();
    const isMobilePlatform = platform === 'ios' || platform === 'android';
    const nonMobilePlatformLoggedRef = useRef(false);
    
    if (!isMobilePlatform) {
        // Only log once per component instance
        if (!nonMobilePlatformLoggedRef.current) {
            Analytics.pushNotificationPromptBlocked({
                'Triggered From': triggeredFrom,
                'Block Reason': 'Non-Mobile Platform',
                'Device Type': platform,
            });
            nonMobilePlatformLoggedRef.current = true;
        }
        
        return null;
    }

    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [promptType, setPromptType] = useState('permission');
    const [showDontShowAgain, setShowDontShowAgain] = useState(false);
    const [hideNutrition, setHideNutrition] = useState(false);
    const [delayedVisible, setDelayedVisible] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState(null);
    const [hasStoredToken, setHasStoredToken] = useState(false);
    const [lastVisibleState, setLastVisibleState] = useState(null);
    const pluginNotAvailableLoggedRef = useRef(false);

    const user = UserStore.getUser();

    const handleTokenRegistration = async (token, source = 'Auto') => {
        if (!token?.value) return;

        console.log(`[${source}] New push token detected, syncing to backend...`);
        store.set(LOCALSTORAGE_KEY_DEVICE_TOKEN, token.value);
        await storeTokenInBackend(token.value, platform);

        Analytics.trackPushNotificationSyncToken({
            Action: source,
            'Device Type': platform,
        });
    };

    useEffect(() => {
        setHideNutrition(user?.preferences?.hide_nutrition || false);

        const timer = setTimeout(() => {
            Analytics.pushNotificationPromptDelayCompleted({
                'Triggered From': triggeredFrom,
                'Delay Duration': delay,
                'Device Type': platform,
                'Visible State': visible,
            });
            setDelayedVisible(true);
        }, delay);

        return () => {
            clearTimeout(timer);
            // Track if delay was cancelled (component unmounted before delay completed)
            if (!delayedVisible && delay > 0) {
                Analytics.pushNotificationPromptDelayCancelled({
                    'Triggered From': triggeredFrom,
                    'Delay Duration': delay,
                    'Device Type': platform,
                    'Visible State': visible,
                });
            }
        };
    }, [delay, triggeredFrom, platform, visible, delayedVisible]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            if (!pluginNotAvailableLoggedRef.current) {
                Analytics.pushNotificationPromptBlocked({
                    'Triggered From': triggeredFrom,
                    'Block Reason': 'Plugin Not Available',
                    'Device Type': platform,
                });
                pluginNotAvailableLoggedRef.current = true;
            }
            
            return;
        }

        const run = async () => {
            await ensureRegistrationListeners(
                (token) => handleTokenRegistration(token, 'Auto Sync'),
                (error) => {
                    console.error('Push registration error:', error);
                    setLoading(false);
                    setVisible(false);
                    
                    Analytics.pushNotificationPromptVisibleUnset({
                        'Triggered From': triggeredFrom,
                        'Reason': 'Registration Listener Error',
                        'Device Type': platform,
                        'Error': error.message,
                    });
                }
            );

            const storedData = store.get(LOCALSTORAGE_KEY) || { count: 0, dontShow: false, lastDismissed: null };
            const storedToken = store.get(LOCALSTORAGE_KEY_DEVICE_TOKEN);
            
            // Update state for analytics
            setHasStoredToken(!!storedToken);
            
            let receive;
            try {
                const permissionResult = await PushNotifications.checkPermissions();
                receive = permissionResult.receive;
                setPermissionStatus(receive);
            } catch (error) {
                console.warn('Permission check failed, hiding prompt to avoid annoyance:', error);
                Analytics.pushNotificationPromptBlocked({
                    'Triggered From': triggeredFrom,
                    'Block Reason': 'Permission Check Failed',
                    'Device Type': platform,
                    'Error': error.message,
                });
                setVisible(false);
                
                Analytics.pushNotificationPromptVisibleUnset({
                    'Triggered From': triggeredFrom,
                    'Reason': 'Permission Check Failed',
                    'Device Type': platform,
                    'Error': error.message,
                });
                
                return;
            }

            if (!force) {
                // Apply 2-day cooldown logic before showing any prompts
                // NOTE: This may be too restrictive - users who click "Not Now" won't see prompts for 2 full days
                // even if they perform many qualifying actions. Consider reducing to 1 day or making it configurable.
                if (storedData.lastDismissed) {
                    const lastDismissedDate = new Date(storedData.lastDismissed);
                    const today = new Date();

                    const timeDifference = today.setHours(0, 0, 0, 0) - lastDismissedDate.setHours(0, 0, 0, 0);
                    const daysPassed = timeDifference / (1000 * 60 * 60 * 24);

                    if (daysPassed < 2) {
                        Analytics.pushNotificationPromptBlocked({
                            'Triggered From': triggeredFrom,
                            'Block Reason': 'Cooldown Period',
                            'Days Since Dismissal': Math.round(daysPassed * 10) / 10,
                            'Device Type': platform,
                        });
                        
                        setVisible(false);
                        
                        Analytics.pushNotificationPromptVisibleUnset({
                            'Triggered From': triggeredFrom,
                            'Reason': 'Cooldown Period',
                            'Device Type': platform,
                            'Days Since Dismissal': Math.round(daysPassed * 10) / 10,
                        });
                        
                        return;
                    }
                }
            }

            // ----- force flow -----
            if (force) {
                if (receive === 'denied') {
                    setPromptType('denied');
                    setVisible(true);
                    
                    Analytics.pushNotificationPromptVisibleSet({
                        'Triggered From': triggeredFrom,
                        'Prompt Type': 'denied',
                        'Device Type': platform,
                        'Delay Duration': delay,
                    });
                    return;
                }

                if (receive === 'prompt' || receive === 'prompt-with-rationale') {
                    requestPushNotifications();
                    return;
                }

                if (receive === 'granted') {
                    await PushNotifications.register();
                    await updatePushInhibitedSetting(false, true);
                    onPermissionResult(true);
                    setVisible(false);
                    
                    Analytics.pushNotificationPromptVisibleUnset({
                        'Triggered From': triggeredFrom,
                        'Reason': 'Force Permission Granted',
                        'Device Type': platform,
                    });
                    
                    return;
                }
            }

            // ----- normal flow -----
            if (storedData.dontShow) {
                Analytics.pushNotificationPromptBlocked({
                    'Triggered From': triggeredFrom,
                    'Block Reason': 'Dont Show Again',
                    'Device Type': platform,
                });
                
                setVisible(false);
                
                Analytics.pushNotificationPromptVisibleUnset({
                    'Triggered From': triggeredFrom,
                    'Reason': 'Dont Show Again',
                    'Device Type': platform,
                });
                
                return;
            }

            // Check permissions first - if user has granted permissions, never show prompt
            if (receive === 'granted') {
                Analytics.pushNotificationPromptBlocked({
                    'Triggered From': triggeredFrom,
                    'Block Reason': 'Already Granted',
                    'Device Type': platform,
                });
                
                // Only register if we don't have a token stored
                if (!storedToken) {
                    try {
                        await PushNotifications.register();
                        console.log('Registered new push token for granted permissions');
                    } catch (error) {
                        console.warn('Token registration failed despite granted permissions:', error);
                    }
                }
                
                setVisible(false);
                
                Analytics.pushNotificationPromptVisibleUnset({
                    'Triggered From': triggeredFrom,
                    'Reason': 'Already Granted',
                    'Device Type': platform,
                });
                
                return;
            }

            // User doesn't have granted permissions
            if (receive === 'denied') {
                setPromptType('denied');
                setVisible(true);
                
                Analytics.pushNotificationPromptVisibleSet({
                    'Triggered From': triggeredFrom,
                    'Prompt Type': 'denied',
                    'Device Type': platform,
                    'Delay Duration': delay,
                });
                return;
            }

            // Permission is 'prompt' - handle inconsistent state
            if (storedToken) {
                // Token exists but permission is 'prompt' - this suggests token is stale
                // Clear the token since permission state indicates it's no longer valid
                store.remove(LOCALSTORAGE_KEY_DEVICE_TOKEN);
                console.log('Cleared stale token due to prompt permission state');
            }

            // Show "Don't Show Again" after two dismissals
            if (storedData.count >= 2) {
                setShowDontShowAgain(true);
            }

            Analytics.pushNotificationPromptEligible({
                'Triggered From': triggeredFrom,
                'Prompt Count': storedData.count + 1,
                'Show Dont Show Again': storedData.count >= 2,
                'Device Type': platform,
            });

            // Only increment count immediately before setting visible to prevent race condition
            store.set(LOCALSTORAGE_KEY, { ...storedData, count: storedData.count + 1 });
            setPromptType('permission');
            setVisible(true);
            
            // Track that we set visible to true - helps debug timing issues
            Analytics.pushNotificationPromptVisibleSet({
                'Triggered From': triggeredFrom,
                'Prompt Type': 'permission',
                'Device Type': platform,
                'Delay Duration': delay,
            });
        };

        run();
    }, [platform, force]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            return;
        }

        const checkPermissionOnResume = async () => {
            const { receive } = await PushNotifications.checkPermissions();

            Analytics.trackPushNotificationPromptResumed({
                'Opt-In Status': receive === 'granted' ? 'Enabled' : 'Denied',
                'Device Type': platform,
            });

            if (receive !== 'denied') {
                await ensureRegistrationListeners(
                    (token) => handleTokenRegistration(token, 'Settings Sync'),
                    (err) => console.error('Post-settings registration error', err)
                );
                await PushNotifications.register();
                setVisible(false);
                
                Analytics.pushNotificationPromptVisibleUnset({
                    'Triggered From': triggeredFrom,
                    'Reason': 'Permission Granted On Resume',
                    'Device Type': platform,
                });
                
                await updatePushInhibitedSetting(false);
                onPermissionResult(true);
            } else {
                await updatePushInhibitedSetting(true);
            }
        };

        const removeAppStateListener = App.addListener('appStateChange', ({ isActive }) => {
            if (isActive && visible && promptType === 'denied') {
                checkPermissionOnResume();
            }
        });

        return () => {
            removeAppStateListener.remove();
        };
    }, [visible, promptType]);

    useEffect(() => {
        if (visible && delayedVisible) {
            Analytics.trackPushNotificationPromptShown({
                'Triggered From': triggeredFrom,
                'Prompt Type': promptType,
                'Device Type': platform,
                'Permission Status': permissionStatus,
                'Has Stored Token': hasStoredToken,
            });
        } else if (visible && !delayedVisible) {
            // Track when prompt is visible but delay hasn't completed yet
            Analytics.pushNotificationPromptDelayPending({
                'Triggered From': triggeredFrom,
                'Delay Duration': delay,
                'Device Type': platform,
            });
        }
    }, [visible, delayedVisible, triggeredFrom, promptType, platform, permissionStatus, hasStoredToken, delay]);

    // Track render blocking only on state changes, not every render
    useEffect(() => {
        const currentVisibleState = visible && delayedVisible;
        
        if (lastVisibleState !== null && lastVisibleState !== currentVisibleState) {
            // State changed from visible to not visible
            if (lastVisibleState === true && currentVisibleState === false) {
                const storedData = store.get(LOCALSTORAGE_KEY) || { count: 0 };
                if (storedData.count > 0) {
                    Analytics.pushNotificationPromptRenderBlocked({
                        'Triggered From': triggeredFrom,
                        'Block Reason': 'Visible State Changed',
                        'Previous State': lastVisibleState,
                        'Current State': currentVisibleState,
                        'Visible': visible,
                        'Delayed Visible': delayedVisible,
                        'Device Type': platform,
                    });
                }
            }
        }
        
        setLastVisibleState(currentVisibleState);
    }, [visible, delayedVisible, triggeredFrom, platform, lastVisibleState]);

    const closePrompt = (action = false, dontShowAgain = false) => {
        const storedData = store.get(LOCALSTORAGE_KEY) || { count: 0, dontShow: false, lastDismissed: null };

        if (action === 'Not Now') {
            storedData.lastDismissed = new Date().toISOString();
        }

        if (dontShowAgain) {
            storedData.dontShow = true;
        }

        store.set(LOCALSTORAGE_KEY, storedData);

        setVisible(false);
        setLoading(false);
        
        Analytics.pushNotificationPromptVisibleUnset({
            'Triggered From': triggeredFrom,
            'Reason': 'User Closed Prompt',
            'Device Type': platform,
            'Action': action || 'Unknown',
            'Dont Show Again': dontShowAgain,
        });
        
        onClose();

        if (action) {
            Analytics.trackPushNotificationPromptClosed({
                Action: action,
                'Device Type': platform,
            });
        }
    };

    const requestPushNotifications = async () => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            console.warn('PushNotifications plugin is not available on this platform.');
            return;
        }
        setLoading(true);

        Analytics.trackPushNotificationPromptAccepted({
            'Triggered From': triggeredFrom,
            'Device Type': platform,
        });

        try {
            ensureRegistrationListeners(
                (token) => handleTokenRegistration(token, 'Manual Prompt'),
                (error) => console.error('Push registration error:', error)
            );
            const permStatus = await PushNotifications.requestPermissions();

            if (permStatus.receive === 'granted') {
                Analytics.trackPushNotificationPermissionGranted({
                    'Opt-In Status': 'Enabled',
                    'Device Type': platform,
                });
            } else {
                Analytics.trackPushNotificationPermissionDenied({
                    'Opt-In Status': 'Denied',
                    'Device Type': platform,
                });
            }

            if (permStatus.receive !== 'granted') {
                console.log('Push notifications permission denied.');
                await updatePushInhibitedSetting(true);
                onPermissionResult(false);
                setLoading(false);
                setVisible(false);
                
                Analytics.pushNotificationPromptVisibleUnset({
                    'Triggered From': triggeredFrom,
                    'Reason': 'Permission Denied By User',
                    'Device Type': platform,
                    'Permission Status': permStatus.receive,
                });
                
                return;
            }
            await updatePushInhibitedSetting(false, true);
            await PushNotifications.register();
            onPermissionResult(true);
            closePrompt();
        } catch (error) {
            console.error('Error in push notification setup:', error);
            onPermissionResult(false);
            setLoading(false);
            setVisible(false);
            
            Analytics.pushNotificationPromptVisibleUnset({
                'Triggered From': triggeredFrom,
                'Reason': 'Request Permission Error',
                'Device Type': platform,
                'Error': error.message,
            });
            
            throw error;
        }
    };

    if (!visible || !delayedVisible) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className="el-push-notification-prompt">
            {promptType === 'permission' ? (
                <Prompt
                    buttons={[
                        {
                            label: loading ? 'Enabling ' : 'Enable Notifications',
                            onClick: requestPushNotifications,
                            variant: 'default',
                            disabled: loading,
                            format: 'wide',
                            size: 'large',
                            isLoading: loading,
                        },
                        {
                            label: 'Not Now',
                            onClick: () => closePrompt('Not Now'),
                            variant: 'outline',
                            format: 'wide',
                            size: 'large',
                            disabled: loading,
                        },
                        showDontShowAgain &&
                            !force && {
                                label: 'Don’t Show Again',
                                onClick: () => closePrompt('Don’t Show Again', true),
                                variant: 'outline',
                                className: 'el-btn el-large-btn el-raspberry-outline-btn link',
                                format: 'wide',
                                size: 'large',
                                disabled: loading,
                            },
                    ].filter(Boolean)}
                    onClose={() => closePrompt('App Close')}
                >
                    {loading ? (
                        <div className="loading">
                            <Icon name="spinner2" />
                        </div>
                    ) : (
                        <>
                            <h2>Stay on Track with Meal Reminders</h2>
                            <p>Enable notifications to help you achieve your nutrition goals:</p>
                            <ul>
                                <li>
                                    <span className="icon">
                                        <Icon name="messages"></Icon>
                                    </span>
                                    <span className="text">
                                        Daily menu reminders to get you excited about what’s cooking
                                    </span>
                                </li>
                                <li>
                                    <span className="icon">
                                        <Icon name="schedule3"></Icon>
                                    </span>
                                    <span className="text">
                                        Weekly meal planning and grocery reminders to save time
                                    </span>
                                </li>
                                {!hideNutrition && (
                                    <li>
                                        <span className="icon">
                                            <Icon name="meal-plan"></Icon>
                                        </span>
                                        <span className="text">Meal logging reminders to track your progress</span>
                                    </li>
                                )}
                            </ul>
                        </>
                    )}
                </Prompt>
            ) : (
                <Prompt
                    buttons={[
                        {
                            label: 'Not Now',
                            onClick: () => closePrompt('Not Now'),
                            variant: 'outline',
                            format: 'wide',
                            size: 'large',
                        },
                        showDontShowAgain &&
                            !force && {
                                label: 'Don’t Show Again',
                                onClick: () => closePrompt('Don’t Show Again', true),
                                variant: 'outline',
                                className: 'el-btn el-large-btn el-raspberry-outline-btn link',
                                format: 'wide',
                                size: 'large',
                            },
                    ].filter(Boolean)}
                    onClose={() => closePrompt('App Close')}
                >
                    {renderDeniedContent()}
                </Prompt>
            )}
        </div>,
        document.getElementById('root')
    );
};

export default PushNotificationPrompt;
